/**
 * SortableTree projection (dnd-kit's official example, adapted): given the flattened rows
 * of a list, the active row, the row it's over and the horizontal drag offset, compute
 * the projected depth and parent. Depth 0 = direct child of the list root.
 */
import { arrayMove } from '@dnd-kit/sortable'

export interface FlatRow {
  id: string
  depth: number
  parentId: string // effective parent (the list root for depth 0)
}

export interface Projection {
  depth: number
  parentId: string
  index: number
}

export function projectDrop(
  items: FlatRow[],
  rootId: string,
  activeId: string,
  overId: string,
  offsetX: number,
  indentWidth: number,
  maxDepthLimit: number,
): Projection | null {
  const overIndex = items.findIndex((i) => i.id === overId)
  const activeIndex = items.findIndex((i) => i.id === activeId)
  if (overIndex < 0 || activeIndex < 0) return null
  const active = items[activeIndex]!
  const moved = arrayMove(items, activeIndex, overIndex)
  const prev = moved[overIndex - 1]
  const next = moved[overIndex + 1]
  const dragDepth = Math.round(offsetX / indentWidth)
  const projected = active.depth + dragDepth
  const maxDepth = Math.min(maxDepthLimit, prev ? prev.depth + 1 : 0)
  const minDepth = next ? Math.min(next.depth, maxDepth) : 0
  let depth = projected
  if (projected >= maxDepth) depth = maxDepth
  else if (projected < minDepth) depth = minDepth

  let parentId = rootId
  if (depth > 0 && prev) {
    if (depth === prev.depth) parentId = prev.parentId
    else if (depth > prev.depth) parentId = prev.id
    else {
      const found = moved
        .slice(0, overIndex)
        .reverse()
        .find((i) => i.depth === depth)
      parentId = found?.parentId ?? rootId
    }
  }
  return { depth, parentId, index: overIndex }
}

/** Remove the descendants of `id` (they travel with their parent while dragging). */
export function withoutDescendants(items: FlatRow[], id: string): FlatRow[] {
  const i = items.findIndex((r) => r.id === id)
  if (i < 0) return items
  const d = items[i]!.depth
  let j = i + 1
  while (j < items.length && items[j]!.depth > d) j++
  return [...items.slice(0, i + 1), ...items.slice(j)]
}

/** Siblings around the projected slot → position for core.moveItem. */
export function dropPosition(moved: FlatRow[], index: number, depth: number, parentId: string): { afterId?: string; beforeId?: string } {
  for (let i = index - 1; i >= 0; i--) {
    const r = moved[i]!
    if (r.depth < depth) break
    if (r.depth === depth && r.parentId === parentId) return { afterId: r.id }
  }
  for (let i = index + 1; i < moved.length; i++) {
    const r = moved[i]!
    if (r.depth < depth) break
    if (r.depth === depth && r.parentId === parentId) return { beforeId: r.id }
  }
  return {}
}
